import React, { memo, useState } from 'react';
import styles from './Cells.module.css';
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { setShowFormPopup } from '../../../containers/planning/planningSlice'
import { checkIfPublicHoliday, checkPermission, getUserPreferences } from '../../../app/utils/helpers';
import { config } from '../../../app/utils/config';
import moment from 'moment';
import AddIcon from '@mui/icons-material/Add';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

export const ShiftPlaceholder = memo(function ShiftPlaceholder(props) {

    const dispatch = useDispatch()
    const sampleShifts = useSelector((state) => state.sampleShifts.list)
    const sampleLeaves = useSelector((state) => state.sampleLeaves.list)
    const userId = useSelector((state) => state.login.userId)
    const holidays = useSelector((state) => state.settings.holidays)
    const teamsList = useSelector((state) => state.teams.list)
    const [popoverAnchor, setPopoverAnchor] = useState(null)
    const [hovered, setHovered] = useState(false)

    const canEdit = checkPermission(config.scopes.shifts, config.permissionTypes.edit, props.idTeam)

    // Mirror the header day-off rule (Scheduler.checkDayOff): weekend or public holiday for the team's country
    function checkDayOff() {
        if (!props.data.dateTime) return false
        const date = moment(props.data.dateTime, "x")
        const weekDay = date.day()
        if (weekDay === 0 || weekDay === 6) return true
        const team = teamsList && teamsList.find(t => t.id == props.idTeam)
        return checkIfPublicHoliday(holidays[date.year()], date, team ? team.id_country : 1)
    }

    const isDayOff = props.data.emptyDay ? checkDayOff() : false

    const handleClick = event => {
        if (!canEdit) return
        if (props.data.emptyDay) {
            setPopoverAnchor(event.currentTarget)
        } else if (event.detail === 2) {
            dispatch(setShowFormPopup({ type: props.data.timeFrom ? "SAMPLE_SHIFT" : "SHIFT", data: props.data }))
        }
    }

    const handleSelectShift = (shift) => {
        setPopoverAnchor(null)
        if (props.onSelectSampleShift) {
            props.onSelectSampleShift(shift)
        }
    }

    function handleContextMenu(event) {
        event.preventDefault()
    }

    // Match the drag-and-drop samples row ordering: user-defined order, then favorites first.
    let allSamples = [
        ...(sampleShifts || []),
        ...(sampleLeaves || []).filter(s => s.visibility)
    ]
    const samplesUserOrder = getUserPreferences(userId, "SHIFTS_SAMPLES_ORDER")
    if (samplesUserOrder) {
        allSamples = allSamples.sort((a, b) =>
            samplesUserOrder.indexOf(`${a["id"]}`) - samplesUserOrder.indexOf(`${b["id"]}`)
        )
    }
    // Favorites float to the front (stable sort keeps the existing order within each group)
    allSamples = allSamples.sort((a, b) => (b.is_favorite === true ? 1 : 0) - (a.is_favorite === true ? 1 : 0))

    return (
        <>
            <div
                key={props.key}
                className={styles.shiftPlaceholder + (props.data.emptyDay ? " " + (isDayOff ? styles.shiftPlaceholderEmptyOff : styles.shiftPlaceholderEmpty) : "")}
                onClick={handleClick}
                onContextMenu={handleContextMenu}
                onMouseEnter={() => props.data.emptyDay && canEdit && setHovered(true)}
                onMouseLeave={() => props.data.emptyDay && canEdit && setHovered(false)}
                style={props.data.emptyDay ? { display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: canEdit ? 'pointer' : 'default' } : undefined}
            >
                {props.data.emptyDay && hovered && (
                    <AddIcon style={{ fontSize: 18, color: isDayOff ? 'rgba(92, 107, 192, 0.85)' : 'rgba(211, 47, 47, 0.65)', pointerEvents: 'none' }} />
                )}
            </div>
            {props.data.emptyDay && (
                <Popover
                    open={Boolean(popoverAnchor)}
                    anchorEl={popoverAnchor}
                    onClose={() => setPopoverAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    PaperProps={{
                        sx: {
                            borderRadius: '10px',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.13)',
                            maxHeight: 280,
                            overflow: 'auto',
                            minWidth: 150,
                        }
                    }}
                >
                    <List dense disablePadding>
                        {allSamples.map(shift => {
                            const fmt = (mins) => {
                                if (mins == null) return null
                                const h = String(Math.floor(mins / 60) % 24).padStart(2, '0')
                                const m = String(mins % 60).padStart(2, '0')
                                return `${h}:${m}`
                            }
                            const timeLabel = (shift.timeFrom != null && shift.timeTo != null)
                                ? `${fmt(shift.timeFrom)} – ${fmt(shift.timeTo)}`
                                : null
                            return (
                                <ListItemButton
                                    key={shift.id}
                                    onClick={() => handleSelectShift(shift)}
                                    sx={{ py: 0.75, px: 1.5, gap: 1, display: 'flex', alignItems: 'center' }}
                                >
                                    <Box sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        backgroundColor: shift.color || '#999',
                                        flexShrink: 0,
                                    }} />
                                    <Typography sx={{ fontSize: '0.82rem', flex: 1 }}>
                                        {shift.label || shift.type}
                                    </Typography>
                                    {timeLabel && (
                                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', flexShrink: 0, ml: 1 }}>
                                            {timeLabel}
                                        </Typography>
                                    )}
                                </ListItemButton>
                            )
                        })}
                    </List>
                </Popover>
            )}
        </>
    )
})
